/**
 * ProfileButton - [Phase 2a.2-B §4.7 R8a]
 *
 * Inlined into the three existing headers (Dashboard, DraftRoom, SeasonShell)
 * pending the Phase 2b extraction of a shared <TopBar /> (TD-31).
 *
 * Renders a pill button with the user's first name; clicking opens a small
 * dropdown with display name / email / Log Out.
 */
import { useState, useEffect, useRef } from "react";
import { User as UserIcon, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function ProfileButton({ variant = "dark" }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!user) return null;

  const isLight = variant === "light";
  const btnBg = isLight ? "bg-black/[0.06] hover:bg-black/[0.10] border-black/[0.10]" : "bg-white/[0.06] hover:bg-white/[0.12] border-white/[0.10]";
  const btnText = isLight ? "text-[#0A0A0A]" : "text-[#F5F5F5]";
  const menuBg = isLight ? "bg-[#F5F5F5] border-black/[0.10]" : "bg-[#1C1C1C] border-white/[0.10]";
  const menuText = isLight ? "text-[#0A0A0A]" : "text-[#F5F5F5]";
  const menuMuted = isLight ? "text-[rgba(0,0,0,0.50)]" : "text-[rgba(255,255,255,0.45)]";

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="profile-btn"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 ${btnBg} ${btnText} border font-display font-bold uppercase text-xs px-3 py-1.5 rounded-full transition-all`}
      >
        <UserIcon size={12} />
        <span className="hidden sm:inline">{user.first_name}</span>
      </button>
      {open && (
        <div
          data-testid="profile-dropdown"
          className={`absolute right-0 top-full mt-2 w-64 ${menuBg} border rounded-xl shadow-2xl py-2 z-50`}
        >
          <div className="px-4 py-2">
            <p className={`font-display font-bold text-sm ${menuText}`}>{user.first_name} {user.last_name}</p>
            <p className={`font-body text-xs ${menuMuted} truncate`}>{user.email}</p>
          </div>
          <div className={`border-t ${isLight ? "border-black/[0.06]" : "border-white/[0.06]"} my-1`} />
          <button
            onClick={logout}
            data-testid="profile-logout"
            className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm font-body ${menuText} hover:bg-[#FF6B00]/10 transition-colors`}
          >
            <LogOut size={14} /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
